import type {
  EntityActionViewModel,
  EntityActivityFeedGroupViewModel,
  EntityActivityFeedItemViewModel,
  EntityActivityFeedViewModel,
  EntityCapabilityViewModel,
  EntityCardViewModel,
  EntitySummaryViewModel,
  EntityType
} from "@/shared/types/entity-view";
import { isToolResult, isToolResultOk } from "./types/tool-result";

const ENTITY_CARD_TOOLS = new Set([
  "getClaimById",
  "getMyClaims",
  "getMyTasks",
  "getTaskById",
  "getContactById",
  "getCompanyById",
  "queryClaimMosaicData"
]);

const ENTITY_SUMMARY_TOOLS = new Set([
  "getClaimActivities",
  "getClaimEmailActivities"
]);

const ENTITY_FEED_TOOLS = new Set(["getClaimActivityFeed"]);

function unwrapSuccessfulToolOutput(output: unknown): unknown {
  if (isToolResultOk(output)) {
    return output.data;
  }

  if (isToolResult(output) && !output.ok) {
    return undefined;
  }

  return output;
}

function asObject(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toText(value: unknown, fallback = "Unknown"): string {
  if (value == null) {
    return fallback;
  }

  const text = String(value).trim();
  return text.length > 0 ? text : fallback;
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return null;
}

function toBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") {
    return value;
  }
  return undefined;
}

function optionalProp<K extends string, V>(
  key: K,
  value: V | undefined
): Partial<Record<K, V>> {
  if (value === undefined) {
    return {};
  }
  return { [key]: value } as Record<K, V>;
}

function formatAddress(entity: Record<string, unknown>): string {
  const parts = [
    entity.street1,
    entity.street2,
    entity.city,
    entity.state,
    entity.postalCode
  ]
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean);

  if (parts.length > 0) {
    return parts.join(", ");
  }

  const directAddress = toText(entity.address, "");
  return directAddress || "No address provided";
}

function toPriorityLabel(priority: unknown): string {
  if (priority === 2) return "High";
  if (priority === 1) return "Medium";
  if (priority === 0) return "Low";
  return "Unknown";
}

function normalizeToolName(toolType?: string): string {
  if (!toolType) {
    return "";
  }

  const withoutPrefix = toolType.replace(/^tool-/, "");
  const separatorIndex = withoutPrefix.indexOf("__");

  return separatorIndex === -1
    ? withoutPrefix
    : withoutPrefix.substring(separatorIndex + 2);
}

function normalizeEntityType(value: unknown): EntityType | null {
  if (value === "claim" || value === "task" || value === "contact" || value === "company") {
    return value;
  }
  return null;
}

function isClaimLike(value: unknown): value is Record<string, unknown> {
  const obj = asObject(value);
  if (!obj) {
    return false;
  }

  return (
    (typeof obj.claimNumber === "string" && obj.claimNumber.trim().length > 0) ||
    (typeof obj.claimStatusName === "string" && obj.claimStatusName.trim().length > 0)
  );
}

function isTaskLike(value: unknown): value is Record<string, unknown> {
  const obj = asObject(value);
  if (!obj) {
    return false;
  }

  return (
    (typeof obj.title === "string" && obj.title.trim().length > 0) ||
    obj.priority != null ||
    obj.dueDate != null
  );
}

function isContactLike(value: unknown): value is Record<string, unknown> {
  const obj = asObject(value);
  if (!obj) {
    return false;
  }

  return (
    typeof obj.fullName === "string" ||
    typeof obj.firstName === "string" ||
    typeof obj.lastName === "string"
  );
}

function isCompanyLike(value: unknown): value is Record<string, unknown> {
  const obj = asObject(value);
  if (!obj) {
    return false;
  }

  const companyName = obj.companyName ?? obj.name;
  return typeof companyName === "string" && companyName.trim().length > 0;
}

function inferEntityType(value: unknown): EntityType | null {
  if (isClaimLike(value)) {
    return "claim";
  }
  if (isTaskLike(value)) {
    return "task";
  }
  if (isContactLike(value)) {
    return "contact";
  }
  if (isCompanyLike(value)) {
    return "company";
  }
  return null;
}

function toEntityAction(value: unknown): EntityActionViewModel | null {
  const obj = asObject(value);
  if (!obj) {
    return null;
  }

  const actionId = toText(obj.actionId, "");
  const label = toText(obj.label, "");
  if (!actionId || !label) {
    return null;
  }

  const kind = obj.kind === "write" ? "write" : "read";
  const metadataObj = asObject(obj.metadata);
  const metadata: Record<string, string> = {};
  if (metadataObj) {
    for (const [key, raw] of Object.entries(metadataObj)) {
      metadata[key] = toText(raw, "");
    }
  }

  return {
    actionId,
    label,
    kind,
    enabled: toBoolean(obj.enabled) ?? true,
    metadata,
    ...optionalProp("requiresApproval", toBoolean(obj.requiresApproval)),
    ...optionalProp("destructive", toBoolean(obj.destructive)),
    ...optionalProp("capabilityKey", toText(obj.capabilityKey, "") || undefined),
    ...optionalProp("endpoint", toText(obj.endpoint, "") || undefined),
    ...optionalProp("method", toText(obj.method, "") || undefined)
  };
}

function toEntityCapability(value: unknown): EntityCapabilityViewModel | null {
  const obj = asObject(value);
  if (!obj) {
    return null;
  }

  const key = toText(obj.key, "");
  if (!key) {
    return null;
  }

  return {
    key,
    enabled: toBoolean(obj.enabled) ?? false,
    ...optionalProp("reason", toText(obj.reason, "") || undefined)
  };
}

function mapEnvelopeCard(output: unknown): EntityCardViewModel | null {
  const envelope = extractRichEnvelope(output);
  if (!envelope) {
    return null;
  }

  const card = asObject(envelope.card);
  if (!card) {
    return null;
  }

  const entityType =
    normalizeEntityType(card.entityType) ??
    normalizeEntityType(envelope.entityType);

  if (!entityType) {
    return null;
  }

  const fieldsObj = asObject(card.fields) ?? {};
  const secondaryFieldsObj = asObject(card.secondaryFields) ?? {};

  const fields = Object.entries(fieldsObj).map(([label, value]) => ({
    label,
    value: toText(value)
  }));

  const secondaryFields = Object.entries(secondaryFieldsObj).map(
    ([label, value]) => ({
      label,
      value: toText(value)
    })
  );

  const actions = asArray(card.actions)
    .map((action) => toEntityAction(action))
    .filter((action): action is EntityActionViewModel => action !== null);

  const capabilities = asArray(envelope.capabilities)
    .map((capability) => toEntityCapability(capability))
    .filter((capability): capability is EntityCapabilityViewModel => capability !== null);

  return {
    entityType,
    id: toNumber(card.entityId ?? envelope.entityId),
    ref: toText(card.entityRef ?? envelope.entityRef, "Unknown"),
    title: toText(card.title, "Unknown"),
    subtitle: toText(card.subtitle, ""),
    status: toText(card.status, ""),
    description: toText(card.description, ""),
    fields,
    secondaryFields,
    actions,
    capabilities,
    ...optionalProp("statusColor", toText(card.statusColor, "") || undefined),
    ...optionalProp("statusTextColor", toText(card.statusTextColor, "") || undefined)
  };
}

function mapClaim(value: unknown): EntityCardViewModel | null {
  if (!isClaimLike(value)) {
    return null;
  }

  const claim = value as Record<string, unknown>;
  const policyholder = asObject(claim.policyholder);

  return {
    entityType: "claim",
    id: toNumber(claim.id),
    ref: toText(claim.claimNumber, "Unknown Claim"),
    title: toText(policyholder?.fullName, "Unknown policyholder"),
    subtitle: toText(claim.lossDescription, "No loss description"),
    status: toText(claim.claimStatusName, "Unknown Status"),
    description: toText(claim.lossDescription, "No loss description"),
    fields: [
      {
        label: "Date of Loss",
        value: toText(claim.dateOfLoss, "Unknown")
      },
      {
        label: "Loss Type",
        value: toText(claim.lossType, "Unknown")
      },
      {
        label: "Policy Number",
        value: toText(claim.policyNo, "Unavailable")
      }
    ],
    secondaryFields: [
      {
        label: "Email",
        value: toText(policyholder?.email, "Unavailable")
      },
      {
        label: "Phone",
        value: toText(policyholder?.phoneNumber, "Unavailable")
      },
      {
        label: "Address",
        value: formatAddress(policyholder ?? claim)
      }
    ],
    ...optionalProp("statusColor", toText(claim.claimStatusColor, "") || undefined),
    ...optionalProp("statusTextColor", toText(claim.claimStatusTextColor, "") || undefined)
  };
}

function mapTask(value: unknown): EntityCardViewModel | null {
  if (!isTaskLike(value)) {
    return null;
  }

  const task = value as Record<string, unknown>;
  const taskId = toNumber(task.id);
  const taskTitle = toText(task.title, "Untitled Task");

  return {
    entityType: "task",
    id: taskId,
    ref: taskId != null ? `Task ${taskId}` : taskTitle,
    title: taskTitle,
    subtitle: toText(task.description, "No description"),
    status: toPriorityLabel(task.priority),
    description: toText(task.notes, ""),
    fields: [
      {
        label: "Due Date",
        value: toText(task.dueDate, "No due date")
      },
      {
        label: "Claim",
        value: toText(task.claimNumber, "Unlinked")
      },
      {
        label: "Lead",
        value: toText(task.leadContact, "Unlinked")
      }
    ],
    secondaryFields: [
      {
        label: "Priority",
        value: toPriorityLabel(task.priority)
      }
    ]
  };
}

function getContactName(contact: Record<string, unknown>): string {
  const fullName = toText(contact.fullName, "");
  if (fullName) {
    return fullName;
  }

  const firstName = toText(contact.firstName, "");
  const lastName = toText(contact.lastName, "");
  const combined = `${firstName} ${lastName}`.trim();
  return combined || "Unknown Contact";
}

function mapContact(value: unknown): EntityCardViewModel | null {
  if (!isContactLike(value)) {
    return null;
  }

  const contact = value as Record<string, unknown>;
  const name = getContactName(contact);

  return {
    entityType: "contact",
    id: toNumber(contact.id),
    ref: name,
    title: name,
    subtitle: toText(contact.email, "No email"),
    fields: [
      {
        label: "Email",
        value: toText(contact.email, "Unavailable")
      },
      {
        label: "Phone",
        value: toText(contact.phoneNumber, "Unavailable")
      },
      {
        label: "Address",
        value: formatAddress(contact)
      }
    ]
  };
}

function mapCompany(value: unknown): EntityCardViewModel | null {
  if (!isCompanyLike(value)) {
    return null;
  }

  const company = value as Record<string, unknown>;
  const companyName = toText(company.companyName ?? company.name, "Unknown Company");

  return {
    entityType: "company",
    id: toNumber(company.id),
    ref: companyName,
    title: companyName,
    subtitle: toText(company.industry ?? company.type, ""),
    status: toText(company.status, ""),
    fields: [
      {
        label: "Email",
        value: toText(company.email, "Unavailable")
      },
      {
        label: "Phone",
        value: toText(company.phoneNumber ?? company.phone, "Unavailable")
      },
      {
        label: "Address",
        value: formatAddress(company)
      }
    ],
    secondaryFields: [
      {
        label: "Website",
        value: toText(company.website, "Unavailable")
      }
    ]
  };
}

function mapByType(entityType: EntityType, value: unknown): EntityCardViewModel | null {
  if (entityType === "claim") return mapClaim(value);
  if (entityType === "task") return mapTask(value);
  if (entityType === "contact") return mapContact(value);
  return mapCompany(value);
}

function extractNestedArrays(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }

  const obj = asObject(value);
  if (!obj) {
    return [];
  }

  const directArrays = [obj.results, obj.items, obj.data, obj.value];
  for (const candidate of directArrays) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
}

function toQueryCollections(output: unknown): Record<EntityType, unknown[]> {
  const collections: Record<EntityType, unknown[]> = {
    claim: [],
    task: [],
    contact: [],
    company: []
  };

  if (Array.isArray(output)) {
    for (const item of output) {
      const inferred = inferEntityType(item);
      if (inferred) {
        collections[inferred].push(item);
      }
    }
    return collections;
  }

  const obj = asObject(output);
  if (!obj) {
    return collections;
  }

  const mappedKeys: Array<[EntityType, unknown]> = [
    ["claim", obj.claims],
    ["task", obj.tasks],
    ["contact", obj.contacts],
    ["company", obj.companies]
  ];

  for (const [type, candidate] of mappedKeys) {
    if (Array.isArray(candidate)) {
      collections[type].push(...candidate);
    }
  }

  for (const item of extractNestedArrays(output)) {
    const inferred = inferEntityType(item);
    if (inferred) {
      collections[inferred].push(item);
    }
  }

  const dataObj = asObject(obj.data);
  if (dataObj) {
    for (const [type, candidate] of [
      ["claim", dataObj.claims],
      ["task", dataObj.tasks],
      ["contact", dataObj.contacts],
      ["company", dataObj.companies]
    ] as Array<[EntityType, unknown]>) {
      if (Array.isArray(candidate)) {
        collections[type].push(...candidate);
      }
    }
  }

  return collections;
}

function dedupeEntityCards(cards: EntityCardViewModel[]): EntityCardViewModel[] {
  const seen = new Set<string>();

  return cards.filter((card) => {
    const key = `${card.entityType}:${card.id ?? "none"}:${card.ref}`.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function extractRichEnvelope(output: unknown): Record<string, unknown> | null {
  const obj = asObject(output);
  if (!obj) {
    return null;
  }

  const nested = asObject(obj.agentUi);
  if (nested && (nested.card || nested.activityFeed || nested.entityType)) {
    return nested;
  }

  if (obj.card || obj.activityFeed || obj.entityType) {
    return obj;
  }

  return null;
}

function mapFeedFromEnvelope(output: unknown): EntityActivityFeedViewModel | null {
  const envelope = extractRichEnvelope(output);
  if (!envelope) {
    return null;
  }

  const feedObj = asObject(envelope.activityFeed);
  if (!feedObj) {
    return null;
  }

  const entityType =
    normalizeEntityType(feedObj.entityType) ??
    normalizeEntityType(envelope.entityType) ??
    "claim";

  const groups = asArray(feedObj.groups)
    .map((group): EntityActivityFeedGroupViewModel | null => {
      const groupObj = asObject(group);
      if (!groupObj) {
        return null;
      }

      const items = asArray(groupObj.items)
        .map((item): EntityActivityFeedItemViewModel | null => {
          const itemObj = asObject(item);
          if (!itemObj) {
            return null;
          }

          const actions = asArray(itemObj.actions)
            .map((action) => toEntityAction(action))
            .filter((action): action is EntityActionViewModel => action !== null);

          const mentions = asArray(itemObj.mentions)
            .map((mention) => {
              const mentionObj = asObject(mention);
              if (!mentionObj) {
                return null;
              }
              const contactId = toNumber(mentionObj.contactId);
              if (contactId == null) {
                return null;
              }
              return {
                contactId,
                label: toText(mentionObj.label, "Contact")
              };
            })
            .filter(
              (mention): mention is { contactId: number; label: string } =>
                mention !== null
            );

          const id = toNumber(itemObj.id);
          const claimId = toNumber(itemObj.claimId);
          if (id == null || claimId == null) {
            return null;
          }

          return {
            id,
            claimId,
            activityType: toText(itemObj.activityType, "Unknown"),
            activityTypeLabel: toText(itemObj.activityTypeLabel, "Activity"),
            iconKey: toText(itemObj.iconKey, "activity"),
            createdWhen: toText(itemObj.createdWhen, ""),
            mentions,
            actions,
            ...optionalProp("displayName", toText(itemObj.displayName, "") || undefined),
            ...optionalProp("details", toText(itemObj.details, "") || undefined),
            ...optionalProp("isQuillData", toBoolean(itemObj.isQuillData)),
            ...optionalProp("isPinned", toBoolean(itemObj.isPinned)),
            ...optionalProp("confidential", toBoolean(itemObj.confidential)),
            ...optionalProp("billable", toBoolean(itemObj.billable)),
            ...optionalProp("contactId", toNumber(itemObj.contactId) ?? undefined),
            ...optionalProp("contactName", toText(itemObj.contactName, "") || undefined),
            ...optionalProp("assignmentId", toNumber(itemObj.assignmentId) ?? undefined),
            ...optionalProp(
              "assignmentCompleted",
              toBoolean(itemObj.assignmentCompleted)
            ),
            ...optionalProp("emailSubject", toText(itemObj.emailSubject, "") || undefined),
            ...optionalProp("emailFrom", toText(itemObj.emailFrom, "") || undefined),
            ...optionalProp("emailTo", toText(itemObj.emailTo, "") || undefined),
            ...optionalProp(
              "attachmentFileName",
              toText(itemObj.attachmentFileName, "") || undefined
            ),
            ...optionalProp(
              "attachmentContentType",
              toText(itemObj.attachmentContentType, "") || undefined
            ),
            ...optionalProp(
              "attachmentContainer",
              toText(itemObj.attachmentContainer, "") || undefined
            ),
            ...optionalProp(
              "attachmentReferenceId",
              toText(itemObj.attachmentReferenceId, "") || undefined
            )
          };
        })
        .filter((item): item is EntityActivityFeedItemViewModel => item !== null);

      return {
        label: toText(groupObj.label, "Recent"),
        items
      };
    })
    .filter((group): group is EntityActivityFeedGroupViewModel => group !== null);

  return {
    entityType,
    total: toNumber(feedObj.total) ?? groups.reduce((sum, group) => sum + group.items.length, 0),
    groups,
    ...optionalProp("entityId", toNumber(feedObj.entityId) ?? undefined)
  };
}

export function isEntityCardToolType(toolType?: string): boolean {
  return ENTITY_CARD_TOOLS.has(normalizeToolName(toolType));
}

export function isEntitySummaryToolType(toolType?: string): boolean {
  return ENTITY_SUMMARY_TOOLS.has(normalizeToolName(toolType));
}

export function isEntityFeedToolType(toolType?: string): boolean {
  return ENTITY_FEED_TOOLS.has(normalizeToolName(toolType));
}

export function isKnownEntityToolType(toolType?: string): boolean {
  return (
    isEntityCardToolType(toolType) ||
    isEntitySummaryToolType(toolType) ||
    isEntityFeedToolType(toolType)
  );
}

export function toEntityCards(
  output: unknown,
  toolType?: string
): EntityCardViewModel[] {
  const resolvedOutput = unwrapSuccessfulToolOutput(output);
  if (!resolvedOutput) {
    return [];
  }

  const envelopeCard = mapEnvelopeCard(resolvedOutput);
  if (envelopeCard) {
    return [envelopeCard];
  }

  const normalized = normalizeToolName(toolType);

  if (normalized === "getClaimById") {
    const claim = mapClaim(resolvedOutput);
    return claim ? [claim] : [];
  }

  if (normalized === "getMyClaims") {
    return dedupeEntityCards(
      asArray(resolvedOutput)
        .map((item) => mapEnvelopeCard(item) ?? mapClaim(item))
        .filter((item): item is EntityCardViewModel => item !== null)
    );
  }

  if (normalized === "getMyTasks") {
    return dedupeEntityCards(
      asArray(resolvedOutput)
        .map((item) => mapEnvelopeCard(item) ?? mapTask(item))
        .filter((item): item is EntityCardViewModel => item !== null)
    );
  }

  if (normalized === "getTaskById") {
    const task = mapTask(resolvedOutput);
    return task ? [task] : [];
  }

  if (normalized === "getContactById") {
    const contact = mapContact(resolvedOutput);
    return contact ? [contact] : [];
  }

  if (normalized === "getCompanyById") {
    const company = mapCompany(resolvedOutput);
    return company ? [company] : [];
  }

  if (normalized === "queryClaimMosaicData") {
    const collections = toQueryCollections(resolvedOutput);
    return dedupeEntityCards(
      (["claim", "task", "contact", "company"] as EntityType[])
        .flatMap((type) =>
          collections[type]
            .map((item) => mapByType(type, item))
            .filter((item): item is EntityCardViewModel => item !== null)
        )
    );
  }

  return [];
}

export function toEntitySummary(
  output: unknown,
  toolType?: string
): EntitySummaryViewModel | null {
  const resolvedOutput = unwrapSuccessfulToolOutput(output);
  if (!resolvedOutput) {
    return null;
  }

  const normalized = normalizeToolName(toolType);

  if (normalized === "getClaimActivities") {
    const activities = asArray(resolvedOutput);
    const rows = activities
      .map((item) => {
        const obj = asObject(item);
        if (!obj) {
          return null;
        }

        const displayName = toText(obj.displayName, "Activity");
        const details = toText(obj.details, "No details");
        return `${displayName}: ${details}`;
      })
      .filter((row): row is string => row !== null)
      .slice(0, 5);

    return {
      entityType: "claim",
      kind: "activity",
      title: "Recent Activities",
      total: activities.length,
      rows
    };
  }

  if (normalized === "getClaimEmailActivities") {
    const emails = asArray(resolvedOutput);
    const rows = emails
      .map((item) => {
        const obj = asObject(item);
        if (!obj) {
          return null;
        }

        const subject = toText(obj.subject, "No subject");
        const from = toText(obj.from, "Unknown sender");
        const to = toText(obj.to, "Unknown recipient");
        return `${subject} | ${from} -> ${to}`;
      })
      .filter((row): row is string => row !== null)
      .slice(0, 5);

    return {
      entityType: "claim",
      kind: "email",
      title: "Recent Emails",
      total: emails.length,
      rows
    };
  }

  return null;
}

export function toEntityActivityFeed(
  output: unknown,
  toolType?: string
): EntityActivityFeedViewModel | null {
  const resolvedOutput = unwrapSuccessfulToolOutput(output);
  if (!resolvedOutput) {
    return null;
  }

  const feed = mapFeedFromEnvelope(resolvedOutput);
  if (feed) {
    return feed;
  }

  const normalized = normalizeToolName(toolType);
  if (normalized === "getClaimActivityFeed") {
    const direct = asObject(resolvedOutput);
    if (!direct) {
      return null;
    }

    return mapFeedFromEnvelope({
      entityType: "claim",
      activityFeed: direct
    });
  }

  return null;
}

export function getNormalizedToolName(toolType?: string): string {
  return normalizeToolName(toolType);
}

export function getToolOutputRenderMode(
  toolType: string | undefined,
  output: unknown,
  errorText: unknown
): "error" | "entity" | "generic" {
  if (errorText) {
    return "error";
  }

  const resolvedOutput = unwrapSuccessfulToolOutput(output);
  if (!resolvedOutput) {
    if (isToolResult(output) && !output.ok) {
      return "error";
    }
    return "generic";
  }

  if (isKnownEntityToolType(toolType)) {
    return "entity";
  }

  return "generic";
}
